import React, { Component, useState, useEffect } from 'react';
import { Animation, Colors } from '../../../theme';
import { isolateComponent, labelStyles, withModifier, } from '@zapier/style-encapsulation';
import { asyncImportV4SVG, asyncImportV5SVG } from './asyncImportSVG';
import { useTheme } from '../../../index';
const sanitizeClassName = (size) => {
    return `${size}`.replace(/[^a-z0-9]/gi, '-');
};
const Styles = labelStyles('Icon', {
    root: (props) => [
        {
            label: `-${props.name}`,
            display: 'inline-block',
            fill: 'currentColor',
            transitionProperty: 'fill',
            transitionTimingFunction: Animation.transitionTimingFunction,
            transitionDuration: Animation.transitionDuration,
            '> svg': {
                display: 'block',
                height: 'inherit',
                width: 'inherit',
            },
            '*': {
                fill: 'inherit',
            },
        },
        !props.canAcceptPointerEvents &&
            withModifier('disable-pointer-events', {
                pointerEvents: 'none',
            }),
        props.isBlock &&
            withModifier('block', {
                display: 'block',
            }),
        withModifier(sanitizeClassName(`${props.size}x${props.size}`), {
            height: props.size,
            width: props.size,
        }),
        props.color &&
            withModifier(props.color, {
                fill: Colors[props.color],
            }),
    ],
});
const LoadingIcon = () => (isolateComponent("svg", { "aria-label": "Loading icon", "data-is-loading-icon": "true", viewBox: "0 0 24 24" }));
const MissingIcon = () => (isolateComponent("svg", { "aria-label": "Could not load icon", "data-is-missing-icon": "true", viewBox: "0 0 24 24" },
    isolateComponent("rect", { height: "24", width: "24", x: "0", y: "0" })));
class IconErrorBoundary extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            didFailToLoad: false,
        };
    }
    static getDerivedStateFromError() {
        return {
            didFailToLoad: true,
        };
    }
    render() {
        return this.state.didFailToLoad ? isolateComponent(MissingIcon, null) : this.props.children;
    }
}
const v4IconsCache = {};
const v5IconsCache = {};
const loadSvgAsComponent = async (isRebrand, name) => {
    if (!isRebrand && v4IconsCache[name]) {
        return v4IconsCache[name];
    }
    if (isRebrand && v5IconsCache[name]) {
        return v5IconsCache[name];
    }
    if (!process.browser) {
        return LoadingIcon;
    }
    try {
        const svg = isRebrand
            ? await asyncImportV5SVG(name)
            : await asyncImportV4SVG(name);
        const Svg = svg.default;
        const props = typeof Svg === 'string'
            ? {
                dangerouslySetInnerHTML: { __html: Svg },
            }
            : {
                children: isolateComponent(Svg, null),
            };
        const whichCache = isRebrand ? v5IconsCache : v4IconsCache;
        whichCache[name] = () => isolateComponent(React.Fragment, Object.assign({}, props));
        return whichCache[name];
    }
    catch (error) {
        const message = `Could not load Icon with name of ${name}`;
        throw new Error(message);
    }
};
export const Icon = (props) => {
    const [IconSvg, setIconSvg] = useState(() => LoadingIcon);
    const { isRebrand } = useTheme();
    useEffect(() => {
        let isMounted = true;
        loadSvgAsComponent(isRebrand, props.name)
            .then((SvgComponent) => {
            if (isMounted) {
                setIconSvg(() => SvgComponent);
            }
        })
            .catch(() => { });
        return () => {
            isMounted = false;
        };
    }, [isRebrand, props.name]);
    return (isolateComponent("span", { "aria-hidden": props.ariaHidden ? 'true' : undefined, "aria-label": props.ariaLabel, css: Styles.root(props), "data-testid": "iconContainer" },
        isolateComponent(IconErrorBoundary, null,
            isolateComponent(IconSvg, null))));
};
Icon.defaultProps = {
    canAcceptPointerEvents: true,
    size: 30,
    ariaHidden: true,
};
//# sourceMappingURL=Icon.js.map
// @zapier/design-system@5.6.0 (4902ab0c1)
